// PDF del informe de un simulacro y del informe anual (PE-B2), con el mismo estilo que el PDF del plan (pe-plan-pdf.ts):
// portada oscura, títulos azules con línea, trazas con estado, método, norma, umbral, valor y fuente; firmas en recuadros;
// encabezado y pie en todas las páginas. Contenido mínimo del informe según el Anexo 1, PO-17 (fecha y hora, objetivo,
// tipo, lugar, duración, participantes y roles, descripción, incidencias, lecciones aprendidas, registro fotográfico y firmas).

import { jsPDF } from 'jspdf';
import type { Traza } from './pe-tipos';
import { ETIQUETA_ESTADO_TRAZA, paraPdf, valorTraza } from './pe-plan-pdf';
import type { FilaAccion } from './pe-acciones';
import { ETIQUETA_ESTADO_ACCION } from './pe-acciones';
import type { Comparacion, FilaSimulacro, ResumenAnual, RolFirma } from './pe-simulacros';
import {
  ETIQUETA_ESTADO, ETIQUETA_METODO_FIRMA, ETIQUETA_ROL_FIRMA, fechaEc, formatoDuracion, MetodoFirma, tiempoTotal, tipoTexto,
} from './pe-simulacros';

export const TITULO_INFORME = 'Informe de simulacro';
export const TITULO_ANUAL = 'Informe anual de simulacros';

export interface FirmaInformePDF { rol: RolFirma; nombre: string; cedula: string; firmadoEn: string; hash: string; imagen: string | null; archivo?: boolean }

const MARGEN = 15;
const ANCHO = 210 - MARGEN * 2;
const ALTO_UTIL = 297 - 20;
const ARRIBA = 22;

// Lienzo común: texto con salto de página, títulos, trazas, portada, firmas y pie.
function lienzo(tituloDoc: string, empresa: string, borrador: boolean) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = ARRIBA;
  const T = (s: string) => paraPdf(s);
  const marca = () => {
    if (!borrador) return;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(80); doc.setTextColor(241, 245, 249);
    doc.text('BORRADOR', 105, 175, { align: 'center', angle: 45 });
    doc.setTextColor(15, 23, 42);
  };
  const nuevaPagina = () => { doc.addPage(); marca(); y = ARRIBA; };
  const espacio = (alto: number) => { if (y + alto > ALTO_UTIL) nuevaPagina(); };
  const texto = (s: string, tam = 9, opc: { negrita?: boolean; sangria?: number; gris?: boolean; color?: [number, number, number] } = {}) => {
    doc.setFont('helvetica', opc.negrita ? 'bold' : 'normal'); doc.setFontSize(tam);
    if (opc.color) doc.setTextColor(...opc.color); else if (opc.gris) doc.setTextColor(100, 116, 139); else doc.setTextColor(15, 23, 42);
    const sangria = opc.sangria ?? 0;
    (doc.splitTextToSize(T(s), ANCHO - sangria) as string[]).forEach(l => { espacio(tam * 0.45); doc.text(l, MARGEN + sangria, y); y += tam * 0.45; });
    doc.setTextColor(15, 23, 42);
  };
  const titulo = (s: string) => {
    espacio(14); y += 3;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11.5); doc.setTextColor(29, 111, 163);
    doc.text(T(s), MARGEN, y);
    doc.setDrawColor(203, 213, 225); doc.line(MARGEN, y + 1.5, MARGEN + ANCHO, y + 1.5);
    y += 6.5; doc.setTextColor(15, 23, 42);
  };
  const colorEstado = (e: Traza['estado']): [number, number, number] =>
    (e === 'no_cumple' ? [185, 28, 28] : e === 'cumple' ? [21, 128, 61] : e === 'por_validar' ? [180, 83, 9] : [71, 85, 105]);
  const traza = (t: Traza) => {
    espacio(14);
    texto(`${ETIQUETA_ESTADO_TRAZA[t.estado]} · ${t.norma} · ${t.articulo_o_tabla}`, 8.5, { negrita: true, color: colorEstado(t.estado), sangria: 2 });
    // PE-B2b: el umbral lleva la misma unidad que el valor (los tiempos ya vienen como "1 min 59 s").
    const unidad = t.unidad && t.unidad !== '—' ? ` ${t.unidad}` : '';
    const conUnidad = (v: number | string | null) => `${valorTraza(v)}${typeof v === 'number' ? unidad : ''}`;
    const umb = t.umbral === null ? '' : `Umbral: ${conUnidad(t.umbral)} · `;
    texto(`Método: ${t.metodo} · ${umb}Valor: ${conUnidad(t.valor)}`, 8, { sangria: 2 });
    if (t.nota) texto(t.nota, 7.5, { gris: true, sangria: 2 });
    texto(`Fuente: ${t.fuente_doc}`, 7, { gris: true, sangria: 2 });
    y += 1;
  };
  // PE-B2b: la etiqueta se ajusta a su columna (58 mm) y el valor empieza en 62 mm: las etiquetas largas pasan a
  // otra línea en vez de encimarse con el valor. La fila ocupa lo que ocupe el más alto de los dos.
  const COL_ETIQUETA = 58, COL_VALOR = 62, LINEA = 4.2;
  const campos = (pares: [string, string][]) => {
    for (const [k, v] of pares) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
      const etiqueta = doc.splitTextToSize(T(`${k}:`), COL_ETIQUETA) as string[];
      doc.setFont('helvetica', 'normal');
      const valor = doc.splitTextToSize(T(v || '—'), ANCHO - COL_VALOR) as string[];
      const lineas = Math.max(etiqueta.length, valor.length);
      espacio(lineas * LINEA + 2);
      const y0 = y;
      doc.setFont('helvetica', 'bold');
      for (let i = 0; i < etiqueta.length; i++) doc.text(etiqueta[i], MARGEN, y0 + i * LINEA);
      doc.setFont('helvetica', 'normal');
      for (let i = 0; i < valor.length; i++) doc.text(valor[i], MARGEN + COL_VALOR, y0 + i * LINEA);
      y += (lineas - 1) * LINEA + 5.2;
    }
  };
  const portada = (subtitulo: string, pares: [string, string][]) => {
    marca();
    doc.setFillColor(15, 23, 42); doc.rect(0, 0, 210, 60, 'F');
    doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(20);
    doc.text(T(tituloDoc), MARGEN, 30);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(12);
    doc.text(T(empresa || 'Empresa'), MARGEN, 41);
    doc.setFontSize(10); doc.text(T(subtitulo), MARGEN, 50);
    doc.setTextColor(15, 23, 42);
    y = 72;
    campos(pares);
  };
  const firmas = (lista: FirmaInformePDF[], metodo: MetodoFirma | null) => {
    // PE-B2b: título, nota y recuadros (unos 75 mm) en la misma página.
    espacio(75);
    titulo('Firmas');
    if (metodo) texto(`Método: ${ETIQUETA_METODO_FIRMA[metodo]}. Firma el responsable de SST; el representante legal es opcional.`, 8, { gris: true });
    espacio(52);
    const anchoCol = (ANCHO - 6) / 2; const y0 = y + 1;
    (['sst', 'representante'] as RolFirma[]).forEach((rol, i) => {
      const f = lista.find(x => x.rol === rol);
      const x = MARGEN + i * (anchoCol + 6);
      doc.setDrawColor(203, 213, 225); doc.rect(x, y0, anchoCol, 24);
      if (f?.imagen) { try { doc.addImage(f.imagen, 'PNG', x + 2, y0 + 1, anchoCol - 4, 22); } catch { /* imagen dañada: queda el recuadro */ } }
      else if (f?.archivo) { doc.setFontSize(8); doc.setTextColor(100, 116, 139); doc.text(T('Firmado en el documento adjunto'), x + 3, y0 + 12); doc.setTextColor(15, 23, 42); }
      doc.setFontSize(8.5); doc.setFont('helvetica', 'bold'); doc.text(T(ETIQUETA_ROL_FIRMA[rol] + (rol === 'representante' ? ' (opcional)' : '')), x, y0 + 28);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
      if (f) {
        doc.text(T(`${f.nombre} · C.I. ${f.cedula}`), x, y0 + 32);
        doc.text(T(new Date(f.firmadoEn).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' })), x, y0 + 36);
        doc.setFontSize(6.5); doc.setTextColor(100, 116, 139);
        doc.text(T(`SHA-256 ${f.hash.slice(0, 32)}…`), x, y0 + 40); doc.setTextColor(15, 23, 42);
      } else doc.text(T(rol === 'sst' ? 'Firma pendiente' : 'Sin firma (opcional)'), x, y0 + 32);
    });
    y = y0 + 46;
  };
  const cerrar = (pie: string) => {
    const paginas = doc.getNumberOfPages();
    for (let n = 1; n <= paginas; n++) {
      doc.setPage(n);
      if (n > 1) {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
        doc.text(T(empresa || ''), MARGEN, 10);
        doc.text(T(tituloDoc), 210 - MARGEN, 10, { align: 'right' });
        doc.setDrawColor(226, 232, 240); doc.line(MARGEN, 12, 210 - MARGEN, 12);
      }
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
      doc.text(T(pie), MARGEN, 290);
      doc.text(T(`Página ${n} de ${paginas}`), 210 - MARGEN, 290, { align: 'right' });
      doc.setTextColor(15, 23, 42);
    }
    return doc;
  };
  return { doc, texto, titulo, traza, campos, portada, firmas, cerrar, gap: (mm: number) => { y += mm; } };
}

const horas = (s: FilaSimulacro) => [
  ['Alarma', s.alarm_time ?? '—'], ['Inicio de la evacuación', s.evacuation_start_time ?? '—'], ['Último en llegar al punto de encuentro', s.last_arrival_time ?? '—'],
] as [string, string][];

export interface DatosInformePDF {
  empresa: string;
  planCodigo: string | null;
  simulacro: FilaSimulacro;
  alcance: string;
  amenaza: string | null;
  coordinador: string;
  comparaciones: Comparacion[];
  brigadistas: string[];
  acciones: FilaAccion[];            // acciones de mejora guardadas de este simulacro
  evidencias: { file_name: string; mime_type: string }[];
  firmas: FirmaInformePDF[];
  metodo: MetodoFirma | null;
  borrador: boolean;
  fecha: Date;
}

export function generarInformeSimulacroPDF(d: DatosInformePDF): jsPDF {
  const s = d.simulacro;
  const L = lienzo(TITULO_INFORME, d.empresa, d.borrador);
  const total = tiempoTotal(s).segundos;
  L.portada(`${tipoTexto(s)} · ${fechaEc(s.performed_date ?? s.drill_date)}`, [
    ['Empresa', d.empresa || '—'], ['Plan de emergencia', d.planCodigo || 'sin código'], ['Estado', ETIQUETA_ESTADO[s.status]],
    ['Fecha programada', fechaEc(s.scheduled_date)], ['Fecha realizada', fechaEc(s.performed_date)], ['Hora de inicio', s.start_time ?? '—'],
    ['Tipo', tipoTexto(s)], ['Amenaza del plan', d.amenaza ?? '—'], ['Lugar', s.location ?? '—'], ['Alcance', d.alcance],
    ['Aviso previo', s.announced === null ? '—' : s.announced ? 'Con aviso previo' : 'Sin aviso'], ['Coordinador', d.coordinador || '—'],
    ['Observadores', s.observers ?? '—'],
  ]);
  L.titulo('1. Objetivo y escenario');
  L.campos([['Objetivo', s.objective ?? '—'], ['Escenario', s.scenario ?? '—']]);
  L.titulo('2. Participantes');
  L.campos([
    ['Participantes', s.participants_count === null ? '—' : String(s.participants_count)],
    ['Ocupantes esperados', s.expected_occupants === null ? '—' : String(s.expected_occupants)],
    ['Conteo en el punto de encuentro', s.assembly_count === null ? '—' : String(s.assembly_count)],
    ['Brigadistas presentes', d.brigadistas.length ? d.brigadistas.join(', ') : 'Ninguno registrado'],
  ]);
  L.titulo('3. Tiempos');
  L.campos([...horas(s), ['Tiempo total (alarma → último en llegar)', formatoDuracion(total)]]);
  L.titulo('4. Comparación con el plan');
  d.comparaciones.forEach(c => L.traza(c.traza));
  L.titulo('5. Hallazgos, lecciones aprendidas y conclusión');
  L.campos([['Hallazgos e incidencias', s.incidents_notes ?? '—'], ['Lecciones aprendidas', s.lessons_learned ?? '—'], ['Conclusión', s.conclusion ?? '—']]);
  L.titulo('6. Acciones de mejora');
  if (!d.acciones.length) L.texto('Sin acciones de mejora guardadas.', 8.5, { gris: true });
  d.acciones.forEach((a, i) => {
    L.texto(`${i + 1}. ${a.description}${a.is_mandatory ? ' (obligatoria)' : ''}`, 8.5, { sangria: 2 });
    L.texto(`Responsable: ${a.responsible_role || (a.responsible_worker_id ? 'trabajador asignado' : 'sin responsable')} · Fecha compromiso: ${a.due_date ? fechaEc(a.due_date) : 'sin fecha'} · Estado: ${ETIQUETA_ESTADO_ACCION[a.status]}`, 8, { gris: true, sangria: 6 });
  });
  L.titulo('7. Registro fotográfico y evidencias');
  if (!d.evidencias.length) L.texto('Sin evidencias adjuntas.', 8.5, { gris: true });
  d.evidencias.forEach((e, i) => L.texto(`${i + 1}. ${e.file_name} (${e.mime_type === 'application/pdf' ? 'PDF' : 'foto'})`, 8.5, { sangria: 2 }));
  L.firmas(d.firmas, d.metodo);
  return L.cerrar(`${TITULO_INFORME} · ${fechaEc(s.performed_date ?? s.drill_date)} · generado ${fechaEc(d.fecha.toISOString())}`);
}

export interface DatosAnualPDF {
  empresa: string;
  planCodigo: string | null;
  resumen: ResumenAnual;
  simulacros: { fecha: string; tipo: string; estado: string; alcance: string; tiempo: string; firmado: boolean; semaforo: string }[];
  frecuencia: Traza[];                       // trazas de PO-17 al cierre del año
  acciones: { descripcion: string; origen: string; estado: string; vencida: boolean }[];
  firmas: FirmaInformePDF[];
  metodo: MetodoFirma | null;
  borrador: boolean;
  fecha: Date;
  version: number;
}

export function generarInformeAnualPDF(d: DatosAnualPDF): jsPDF {
  const r = d.resumen;
  const L = lienzo(`${TITULO_ANUAL} ${r.year}`, d.empresa, d.borrador);
  L.portada(`Año ${r.year} · versión ${d.version}`, [
    ['Empresa', d.empresa || '—'], ['Plan de emergencia', d.planCodigo || 'sin código'], ['Año', String(r.year)],
    ['Simulacros realizados', `${r.realizados} (${r.firmados} con el informe firmado)`], ['Programados sin realizar', String(r.programados)],
    ['Cancelados', String(r.cancelados)], ['Frecuencia mínima (al menos 1 al año)', r.cumpleFrecuencia ? 'Cumple' : 'No cumple'],
  ]);
  L.titulo('1. Cumplimiento de la frecuencia mínima');
  d.frecuencia.forEach(L.traza);
  L.titulo('2. Simulacros del año');
  if (!d.simulacros.length) L.texto('No hubo simulacros en el año.', 8.5, { gris: true });
  d.simulacros.forEach((x, i) => {
    L.texto(`${i + 1}. ${x.fecha} · ${x.tipo} · ${x.estado}${x.firmado ? ' · informe firmado' : ''}`, 8.5, { negrita: true, sangria: 2 });
    L.texto(`Alcance: ${x.alcance} · Tiempo total: ${x.tiempo} · Comparación con el plan: ${x.semaforo}`, 8, { gris: true, sangria: 6 });
  });
  L.titulo('3. Resumen de tiempos');
  L.campos([
    ['Simulacros con tiempo registrado', String(r.tiempos.con_tiempo)], ['Mínimo', formatoDuracion(r.tiempos.minimo_s)],
    ['Máximo', formatoDuracion(r.tiempos.maximo_s)], ['Promedio', formatoDuracion(r.tiempos.promedio_s)],
  ]);
  L.titulo('4. Acciones de mejora');
  L.campos([['Total', String(r.acciones.total)], ['Abiertas', String(r.acciones.abiertas)], ['Cerradas', String(r.acciones.cerradas)], ['Vencidas', String(r.acciones.vencidas)]]);
  d.acciones.forEach((a, i) => L.texto(`${i + 1}. ${a.descripcion} · ${a.origen} · ${a.estado}${a.vencida ? ' (vencida)' : ''}`, 8, { sangria: 2 }));
  L.firmas(d.firmas, d.metodo);
  return L.cerrar(`${TITULO_ANUAL} ${r.year} · versión ${d.version} · generado ${fechaEc(d.fecha.toISOString())}`);
}
